"use client";

import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import type { ReactNode } from "react";
import GradientText from "@/components/GradientText";
import { gridMetrics } from "@/components/hero/HeroGrid";
import { REGISTER_URL } from "@/components/pricing/data";
import { BrowserWindow, Button, CODE, DARK_BAND, SectionShell } from "./kit";
import { routePath } from "@/lib/routes";
import { EASE_OUT, gsap, insure, NOT_REDUCED, useGSAP, useMaskedReveal } from "./kit/motion";

type ClientId = "browser" | "curl" | "agent" | "app";

interface Client {
  id: ClientId;
  badge: string;
  badgeTone: string;
  label: string;
  /** The request detail that decides the response — rides on the packet. */
  header: string;
  status: string;
  statusTone: string;
  caption: string;
  response: ReactNode;
}

/** The mini storefront a browser ends up on after the 302. */
function HumanCard() {
  return (
    <BrowserWindow url="myshop.com/launch">
      <div className="px-5 py-4">
        <div className="text-[17px] font-bold tracking-[-0.01em]">Launch week — 40% off.</div>
        <div className="mt-1 text-[13px] text-muted">Everything in the launch collection.</div>
        <div className="mt-3 h-[64px] rounded-[9px] bg-[linear-gradient(135deg,rgba(215,14,98,0.14),rgba(47,107,224,0.10))]" />
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[19px] font-bold tabular-nums">€49</span>
          <span className="rounded-lg bg-brand-600 px-3.5 py-[7px] text-xs font-semibold text-white">
            Shop now
          </span>
        </div>
      </div>
    </BrowserWindow>
  );
}

function PayloadCard({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0d0d12] p-4">
      <pre className="m-0 min-w-0 whitespace-pre-wrap font-jetbrains text-[13px] leading-[1.75] text-white/85 [overflow-wrap:anywhere]">
        {children}
      </pre>
    </div>
  );
}

const CLIENTS: Client[] = [
  {
    id: "browser",
    badge: "WEB",
    badgeTone: "bg-data-blue/[0.14] text-data-blue",
    label: "Browser",
    header: "no query → default",
    status: "302 Found",
    statusTone: "text-data-amber",
    caption: "GET rii.link/launch → 302 → myshop.com/launch",
    response: <HumanCard />,
  },
  {
    id: "curl",
    badge: "CLI",
    badgeTone: "bg-data-green/[0.14] text-data-green",
    label: "curl",
    header: "?format=json",
    status: "200 OK",
    statusTone: "text-data-green",
    caption: "GET rii.link/launch?format=json → 200 OK",
    response: (
      <PayloadCard>
        {"{"}
        {"\n"}  <span className={CODE.key}>&quot;url&quot;</span>:{" "}
        <span className={CODE.string}>&quot;myshop.com/launch&quot;</span>,{"\n"}  <span className={CODE.key}>&quot;title&quot;</span>:{" "}
        <span className={CODE.string}>&quot;Launch week — 40% off&quot;</span>,{"\n"}  <span className={CODE.key}>&quot;price&quot;</span>:{" "}
        <span className={CODE.number}>49</span>,{"\n"}  <span className={CODE.key}>&quot;og_image&quot;</span>:{" "}
        <span className={CODE.string}>&quot;myshop.com/img/launch.png&quot;</span>
        {"\n"}
        {"}"}
      </PayloadCard>
    ),
  },
  {
    id: "agent",
    badge: "AI",
    badgeTone: "bg-data-violet/[0.16] text-data-violet",
    label: "GPT agent",
    header: "?for=agent",
    status: "200 OK",
    statusTone: "text-data-green",
    caption: "GET rii.link/launch?for=agent → 200 OK",
    response: (
      <PayloadCard>
        {"{"}
        {"\n"}  <span className={CODE.key}>&quot;offer&quot;</span>: {"{"}
        {"\n"}    <span className={CODE.key}>&quot;destination&quot;</span>:{" "}
        <span className={CODE.string}>&quot;myshop.com/launch&quot;</span>,{"\n"}    <span className={CODE.key}>&quot;summary&quot;</span>:{" "}
        <span className={CODE.string}>&quot;Launch-week promo, 40% off&quot;</span>,{"\n"}    <span className={CODE.key}>&quot;code&quot;</span>:{" "}
        <span className={CODE.string}>&quot;LAUNCH40&quot;</span>
        {"\n"}  {"}"}
        {"\n"}
        {"}"}
      </PayloadCard>
    ),
  },
  {
    id: "app",
    badge: "APP",
    badgeTone: "bg-data-amber/[0.16] text-data-amber",
    label: "iOS app",
    header: "?app=ios",
    status: "200 OK",
    statusTone: "text-data-green",
    caption: "GET rii.link/launch?app=ios → 200 OK",
    response: (
      <PayloadCard>
        {"{"}
        {"\n"}  <span className={CODE.key}>&quot;config&quot;</span>: {"{"}
        {"\n"}    <span className={CODE.key}>&quot;min_version&quot;</span>:{" "}
        <span className={CODE.string}>&quot;2.4.0&quot;</span>,{"\n"}    <span className={CODE.key}>&quot;flags&quot;</span>: {"{"}{" "}
        <span className={CODE.key}>&quot;new_checkout&quot;</span>: <span className={CODE.bool}>true</span> {"}"},{"\n"}    <span className={CODE.key}>&quot;deeplink&quot;</span>:{" "}
        <span className={CODE.string}>&quot;myshop://launch&quot;</span>
        {"\n"}  {"}"}
        {"\n"}
        {"}"}
      </PayloadCard>
    ),
  },
];

/** Pause between the end of one cycle and the next departure — the cycle
 *  chains off timeline completion, so the rhythm is EXACTLY
 *  travel + HOLD_MS, every time. */
const HOLD_MS = 2600;
const FIRST_CYCLE_MS = 2200;

/** Impact flash: two shape-following drop-shadows — the tight one reads as a
 *  colored border, the wide one as the glow; works for the white browser
 *  window and the dark payload cards alike. */
const HIT_FILTER =
  "drop-shadow(0 0 1.5px rgba(250,67,127,0.9)) drop-shadow(0 0 26px rgba(250,67,127,0.55))";
const REST_FILTER =
  "drop-shadow(0 0 0px rgba(250,67,127,0)) drop-shadow(0 0 0px rgba(250,67,127,0))";

/**
 * The Negotiation Switchboard — /dynamic-responses' HERO. The page opens on
 * this dark band: h1 + lead + CTAs, with the switchboard stage beneath.
 * Per-request routing as visible causality: pick a client and its request packet
 * (carrying the deciding query parameter as a chip — routing switches on query
 * params, device, OS, browser, language and geo; never on Accept headers)
 * travels client → link node →
 * response panel, which re-renders per client. Idle it auto-cycles through
 * the clients (the WorkflowDemo pattern); the first user click takes over
 * permanently. Normal document flow, no sticky/pin; the stage is a regular
 * [data-grid-block] on the opaque dark band. Under reduced motion there is
 * no autoplay and no packet — clicking swaps the response instantly.
 */
export default function NegotiationSwitchboardSection() {
  const [client, setClient] = useState<ClientId>("browser");
  const active = CLIENTS.find((c) => c.id === client) ?? CLIENTS[0];

  const stageRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const midRef = useRef<HTMLDivElement>(null);
  const nodeRef = useRef<HTMLDivElement>(null);
  const packetRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLSpanElement>(null);
  const interactedRef = useRef(false);
  const visibleRef = useRef(false);
  const tlRef = useRef<gsap.core.Timeline | null>(null);
  const holdRef = useRef<number>(0);
  /** Mirror of the client state for the autoplay chain (no state-setter hacks). */
  const clientRef = useRef<ClientId>(client);
  /** Motion-enabled fire(), set only while the matchMedia scope is active. */
  const fireRef = useRef<((id: ClientId) => void) | null>(null);

  const commitClient = (id: ClientId) => {
    clientRef.current = id;
    setClient(id);
  };

  useGSAP(
    (_context, contextSafe) => {
      const mm = gsap.matchMedia();
      mm.add(`${NOT_REDUCED} and (min-width: 768px)`, () => {
        const fire = contextSafe!((id: ClientId) => {
          const stage = stageRef.current;
          const mid = midRef.current;
          const rail = railRef.current;
          const node = nodeRef.current;
          const packet = packetRef.current;
          const chip = chipRef.current;
          const btn = rail?.querySelector<HTMLElement>(`[data-client="${id}"]`);
          const target = CLIENTS.find((c) => c.id === id);
          if (!stage || !mid || !node || !packet || !chip || !btn || !target) {
            commitClient(id);
            return;
          }
          window.clearTimeout(holdRef.current);
          tlRef.current?.kill();

          // Geometry measured per fire — always current, no stale caches.
          const midRect = mid.getBoundingClientRect();
          const btnRect = btn.getBoundingClientRect();
          const nodeRect = node.getBoundingClientRect();
          const startY = btnRect.top + btnRect.height / 2 - midRect.top;
          const centerY = midRect.height / 2;
          const nodeX = nodeRect.left + nodeRect.width / 2 - midRect.left;
          // Travel far enough past the wire's end that the WHOLE chip (it is
          // centered on the dot, so half of it trails) slides under the
          // response card's edge before it stops — the card's cover does the
          // hiding, no early fade. Chip width is measured after setting its
          // text below.
          chip.textContent = target.header;
          const endX = midRect.width + 36 + chip.offsetWidth / 2 + 24;

          const tl = gsap.timeline({ defaults: { ease: "power1.inOut" } });
          // The old response leaves as the new request departs — the stage is
          // answer-less during flight, so the arriving response is
          // unambiguously the current one.
          tl.to(
            stage.querySelectorAll("[data-sb-swap]"),
            { y: 8, opacity: 0, duration: 0.3, ease: "power1.in", overwrite: "auto" },
            0,
          );
          tl.set(packet, { x: 0, y: startY, autoAlpha: 0 }, 0);
          tl.to(packet, { autoAlpha: 1, duration: 0.25 }, 0.1);
          tl.to(packet, { y: centerY, duration: 0.65, ease: "power2.inOut" });
          tl.to(packet, { x: nodeX, duration: 0.7 });
          // The node "decides" — a brief pulse as the request passes through.
          tl.to(node, { scale: 1.06, duration: 0.18, yoyo: true, repeat: 1, ease: "power2.out" }, "<+0.45");
          tl.to(packet, { x: endX, duration: 0.7, ease: "power1.out" });
          // Arrival — ALL on the timeline's clock, no React-effect timing in
          // the visual path: flushSync commits the swap synchronously, then
          // the settle + impact run imperatively in the same tick, so the
          // response lands at the identical moment every cycle.
          tl.call(
            () => {
              flushSync(() => commitClient(id));
              const swaps = stage.querySelectorAll("[data-sb-swap]");
              gsap.fromTo(
                swaps,
                { y: 10, opacity: 0 },
                {
                  y: 0,
                  opacity: 1,
                  duration: 0.4,
                  ease: EASE_OUT,
                  overwrite: "auto",
                  clearProps: "transform,opacity",
                },
              );
              const activeChild = stage.querySelector("[data-sb-swap] > div:not(.invisible)");
              if (activeChild) {
                gsap
                  .timeline({ defaults: { overwrite: "auto" } })
                  .fromTo(
                    activeChild,
                    { scale: 1, filter: REST_FILTER },
                    { scale: 1.02, filter: HIT_FILTER, duration: 0.22, ease: "power2.out" },
                  )
                  .to(activeChild, {
                    scale: 1,
                    filter: REST_FILTER,
                    duration: 0.65,
                    ease: "power2.inOut",
                    clearProps: "transform,filter",
                  });
              }
            },
            [],
            // Early in the final leg: the new response materializes while
            // the packet approaches, closing the response-less gap.
            "-=0.55",
          );
          tl.set(packet, { autoAlpha: 0 });
          // The next auto-cycle chains off COMPLETION — the pause between
          // cycles is a constant HOLD_MS, never a free-running interval.
          tl.eventCallback("onComplete", scheduleNext);
          insure(tl);
          tlRef.current = tl;
        });
        fireRef.current = fire;

        const fireNext = () => {
          const i = CLIENTS.findIndex((c) => c.id === clientRef.current);
          fire(CLIENTS[(i + 1) % CLIENTS.length].id);
        };
        function scheduleNext() {
          window.clearTimeout(holdRef.current);
          holdRef.current = window.setTimeout(() => {
            if (interactedRef.current) return;
            // Off-screen: don't advance — re-check after another hold.
            if (!visibleRef.current) {
              scheduleNext();
              return;
            }
            fireNext();
          }, HOLD_MS);
        }

        // Idle auto-cycle until the user takes over; paused off-screen.
        const stage = stageRef.current;
        const io = new IntersectionObserver((entries) => {
          visibleRef.current = entries.some((e) => e.isIntersecting);
        });
        if (stage) io.observe(stage);
        const kickoff = window.setTimeout(() => {
          if (!interactedRef.current && visibleRef.current) fireNext();
          else scheduleNext();
        }, FIRST_CYCLE_MS);

        return () => {
          io.disconnect();
          window.clearTimeout(kickoff);
          window.clearTimeout(holdRef.current);
          tlRef.current?.kill();
          tlRef.current = null;
          fireRef.current = null;
        };
      });
    },
    { scope: stageRef },
  );

  const heroRef = useRef<HTMLDivElement>(null);

  // Full-screen grid-true hero geometry (md+):
  // - wrapper height = whole grid rows: viewport rows normally, grown to fit
  //   content when the viewport is short → the dark band always ends on a
  //   row line, no light sliver;
  // - the copy block starts EXACTLY on row 3 (nav = row 1, row 2 free) via
  //   the wrapper's 2-cell top padding; padding lives OUTSIDE the cleared
  //   block so the free row keeps its grid;
  // - the spacer places the stage at the row line nearest the CENTER of the
  //   space below the copy — with minimum one full visible free row BOTH
  //   between the copy and stage clearings AND below the stage before the
  //   band ends (sections always breathe one free row at their edges).
  // Re-measured on resize and after webfonts settle; announces geometry
  // changes so RowBand/GridClearing re-snap.
  const heroWrapRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const stageBlockRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const layout = () => {
      const wrap = heroWrapRef.current;
      const spacer = spacerRef.current;
      const head = heroRef.current;
      const stageBlock = stageBlockRef.current;
      if (!wrap || !spacer || !head || !stageBlock) return;
      if (document.documentElement.clientWidth < 768) {
        wrap.style.minHeight = "";
        spacer.style.height = "";
        head.style.height = "";
        stageBlock.style.height = "";
        return;
      }
      const { cell } = gridMetrics(document.documentElement.clientWidth);
      const prev = `${wrap.style.minHeight}|${spacer.style.height}|${head.style.height}|${stageBlock.style.height}`;
      // Grid rule: blocks span WHOLE rows and content centers INSIDE them.
      // Each block gets one row more than its content needs, so the content
      // always breathes 36–72px from the cleared edges (never flush).
      head.style.height = "";
      stageBlock.style.height = "";
      const headContentH = head.offsetHeight;
      const stageContentH = stageBlock.offsetHeight;
      head.style.height = `${(Math.ceil(headContentH / cell) + 1) * cell}px`;
      stageBlock.style.height = `${(Math.ceil(stageContentH / cell) + 1) * cell}px`;
      const wrapTop = wrap.getBoundingClientRect().top + window.scrollY;
      const headBottom = head.getBoundingClientRect().bottom + window.scrollY - wrapTop;
      const stageH = stageBlock.offsetHeight;
      // Whole-row wrapper height: the viewport's rows, grown if the content
      // (copy + free row + stage + free bottom row) needs more.
      const minTop = headBottom + cell;
      const wrapH =
        Math.max(
          Math.floor(window.innerHeight / cell),
          Math.ceil((minTop + stageH + cell) / cell),
        ) * cell;
      // Stage top = row line nearest the center of the remaining space,
      // clamped so one full free row stays above AND below the stage.
      const maxTop = wrapH - stageH - cell;
      const idealTop = headBottom + (wrapH - headBottom - stageH) / 2;
      const stageTop = Math.min(maxTop, Math.max(minTop, Math.round(idealTop / cell) * cell));
      wrap.style.minHeight = `${wrapH}px`;
      spacer.style.height = `${stageTop - headBottom}px`;
      if (
        `${wrap.style.minHeight}|${spacer.style.height}|${head.style.height}|${stageBlock.style.height}` !==
        prev
      ) {
        window.dispatchEvent(new Event("grid-relayout"));
      }
    };
    layout();
    document.fonts.ready.then(layout).catch(() => {});
    window.addEventListener("resize", layout);
    return () => window.removeEventListener("resize", layout);
  }, []);

  // Hero headline: masked SplitText reveal on the h1 (the FeatureHero
  // treatment); the glow halo hides via autoAlpha while the line masks would
  // clip it and fades back in once the split reverts.
  useMaskedReveal(heroRef, {
    heading: "h1",
    sub: "[data-hero-sub]",
    prepare: (root) => {
      gsap.set(root.querySelector("[data-hero-halo]"), { autoAlpha: 0 });
    },
    onDone: (root) => {
      gsap.to(root.querySelector("[data-hero-halo]"), {
        autoAlpha: 1,
        duration: 0.8,
        ease: "power2.out",
      });
    },
  });

  const select = (id: ClientId) => {
    interactedRef.current = true;
    window.clearTimeout(holdRef.current);
    if (id === client) return;
    (fireRef.current ?? commitClient)(id);
  };

  const responseStack = (
    // items-center: each response centers within the reserved (tallest) area,
    // so shorter payloads sit on the wire instead of hanging at the top.
    <div data-sb-swap className="grid items-center">
      {CLIENTS.map((c) => (
        <div
          key={c.id}
          aria-hidden={c.id !== client}
          className={`w-full min-w-0 [grid-area:1/1] ${c.id === client ? "" : "invisible"}`}
        >
          {c.response}
        </div>
      ))}
    </div>
  );

  const caption = (
    <div data-sb-swap className="mt-3 font-jetbrains text-[12px] text-white/50">
      {active.caption.split("→").map((part, i, arr) => (
        <span key={i}>
          {part}
          {i < arr.length - 1 && <span className="text-white/30">→</span>}
        </span>
      ))}
    </div>
  );

  return (
    <SectionShell
      id="ref-negotiation"
      band="dark"
      decoAvoid
      className="max-md:bg-[#14141c] md:py-0"
    >
      {/* min-height snapped to whole grid rows by the layout effect above —
          the full-viewport dark hero; the next section starts at the fold. */}
      <div ref={heroWrapRef} className="md:pt-[calc(2*var(--grid-cell,72px))]">
        <div>
        {/* Hero head — the page's h1, inside the dark band under the fixed
            navbar (which auto-flips to its dark theme over this surface). */}
        <div
          ref={heroRef}
          data-grid-block
          data-grid-fill={DARK_BAND}
          className="mx-auto mb-10 flex max-w-4xl flex-col items-center gap-6 pt-24 text-center md:mb-0 md:justify-center md:pt-0"
        >
          <div
            data-hero-sub
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.07] px-3.5 py-1.5 text-xs font-medium text-white/80"
            style={{ fontFamily: "var(--font-jetbrains)" }}
          >
            <span className="h-2 w-2 rounded-full bg-brand-500" />
            No redirect required
          </div>

          <h1 className="text-5xl font-extrabold leading-[1.06] tracking-tight text-white md:text-6xl">
            Your link is now an{" "}
            <span className="relative inline-block whitespace-nowrap">
              <span
                aria-hidden="true"
                data-hero-halo
                className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[200%] w-[150%] -translate-x-1/2 -translate-y-1/2 animate-cube-pulse rounded-full blur-[70px] [background:radial-gradient(ellipse,rgba(250,67,127,0.45),transparent_70%)]"
              />
              <GradientText colors={["#d70e62", "#fa437f", "#ff7eae"]} animationSpeed={2} yoyo={false}>
                endpoint.
              </GradientText>
            </span>
          </h1>

          <p data-hero-sub className="max-w-2xl text-lg leading-relaxed text-white/70 md:text-xl">
            On Premium, a short link can answer with the JSON, HTML or XML you write. Browsers
            get the redirect; a query-parameter rule you set up (say ?format=json) serves your data.
          </p>

          <div data-hero-sub className="flex flex-wrap items-center justify-center gap-3">
            <Button href={REGISTER_URL}>Start for free</Button>
            <Button href={routePath("developers")} variant="ghost-light">
              View the API
            </Button>
          </div>
        </div>

        {/* Exactly ONE complete visible grid-cell row between the copy block
            and the stage — height measured/snapped by the layout effect. */}
        <div ref={spacerRef} aria-hidden className="hidden md:block" />

        {/* md+ switchboard stage */}
        <div
          ref={stageBlockRef}
          data-grid-block
          data-grid-fill={DARK_BAND}
          className="hidden md:flex md:flex-col md:justify-center"
        >
          <div ref={stageRef} className="flex items-center gap-5 lg:gap-7">
            {/* Client rail — who is asking. */}
            <div ref={railRef} className="flex w-[240px] shrink-0 flex-col gap-2.5">
              {CLIENTS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-client={c.id}
                  onClick={() => select(c.id)}
                  aria-pressed={c.id === client}
                  className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition ${
                    c.id === client
                      ? "border-brand-500/50 bg-[#242432] shadow-[0_0_18px_rgba(250,67,127,0.18)]"
                      : "border-white/10 bg-[#1b1b26] hover:border-white/25"
                  }`}
                >
                  <span
                    className={`w-[42px] shrink-0 rounded-md py-[3px] text-center font-jetbrains text-[10.5px] font-bold ${c.badgeTone}`}
                  >
                    {c.badge}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[14.5px] font-semibold text-white">{c.label}</span>
                    <span className="block truncate font-jetbrains text-[11px] text-white/45">
                      {c.header}
                    </span>
                  </span>
                </button>
              ))}
            </div>

            {/* The wires + the link node — where the decision happens. */}
            <div ref={midRef} className="relative min-w-0 flex-1 self-stretch">
              <div aria-hidden className="absolute bottom-[12%] left-0 top-[12%] w-px bg-white/[0.08]" />
              <div aria-hidden className="absolute inset-x-0 top-1/2 h-px bg-white/[0.08]" />
              <div className="flex h-full items-center justify-center">
                <div
                  ref={nodeRef}
                  className="relative z-[1] rounded-full border border-white/15 bg-[#1b1b26] px-5 py-3 font-jetbrains text-[13.5px] text-white/90 shadow-[0_0_24px_rgba(250,67,127,0.12)]"
                >
                  rii.link/launch
                  <span
                    data-sb-swap
                    className={`absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap font-jetbrains text-[11.5px] font-semibold ${active.statusTone}`}
                  >
                    {active.status}
                  </span>
                </div>
              </div>
              {/* The request packet + its deciding header, driven by GSAP. */}
              <div
                ref={packetRef}
                aria-hidden
                className="invisible absolute left-0 top-0 z-[2]"
              >
                <span className="absolute -left-[5px] -top-[5px] h-[10px] w-[10px] rounded-full bg-brand-500 shadow-[0_0_14px_var(--color-brand-500)]" />
                <span
                  ref={chipRef}
                  className="absolute -top-[30px] left-0 -translate-x-1/2 whitespace-nowrap rounded-full border border-brand-500/40 bg-[#14141c] px-2.5 py-[3px] font-jetbrains text-[11px] text-brand-500"
                />
              </div>
            </div>

            {/* The response — what comes back. The invisible caption mirror
                above the stack balances the real caption below, so the card's
                visual center lands exactly on the wire. */}
            <div className="relative z-[3] flex w-[38%] max-w-[420px] shrink-0 flex-col justify-center self-stretch">
              <div aria-hidden className="invisible mb-3 font-jetbrains text-[12px]">
                {active.caption}
              </div>
              {responseStack}
              {caption}
            </div>
          </div>
        </div>

        {/* Mobile: client chips + the response, no wires. */}
        <div className="flex flex-col gap-5 md:hidden">
          <div className="flex flex-wrap justify-center gap-2">
            {CLIENTS.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => select(c.id)}
                className={`rounded-[11px] border px-4 py-[9px] text-[14px] font-semibold transition ${
                  c.id === client
                    ? "border-transparent bg-brand-600 text-white"
                    : "border-white/15 bg-white/[0.07] text-white/75 hover:border-white/30 hover:text-white"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="mx-auto w-full max-w-[400px]">
            {responseStack}
            {caption}
          </div>
        </div>
      </div>
      </div>
    </SectionShell>
  );
}
